import { GenerateDPoPTokenParams } from './types'

const base64url = (input: Uint8Array | string): string => {
  let str: string
  if (input instanceof Uint8Array) {
    // Convert Uint8Array to binary string in chunks to avoid call stack issues
    const CHUNK_SIZE = 0x8000 // 32k
    let result = ''
    for (let i = 0; i < input.length; i += CHUNK_SIZE) {
      result += String.fromCharCode.apply(
        null,
        input.subarray(i, i + CHUNK_SIZE) as unknown as number[]
      )
    }
    str = result
  } else {
    str = input
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Generates a new ECDSA (P-256) key pair for DPoP use.
 * Returns an object with the private key (CryptoKey) and public key (JWK).
 */
export async function generateKeyPair() {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'ECDSA',
      namedCurve: 'P-256',
    },
    true, // extractable: true so public key can be exported
    ['sign', 'verify']
  )
  const publicJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey)
  return {
    privateKey: keyPair.privateKey,
    publicJwk,
  }
}

/**
 * Generates a signed DPoP proof JWT.
 * Requires a private key and the corresponding public JWK.
 */
export async function generateDPoPToken({
  privateKey,
  publicJwk,
}: GenerateDPoPTokenParams) {
  const header = {
    alg: 'ES256',
    typ: 'dpop+jwt',
    jwk: {
      kty: publicJwk.kty,
      crv: publicJwk.crv,
      x: publicJwk.x,
      y: publicJwk.y,
    },
  }
  const payload = {
    jti: crypto.randomUUID(),
    iat: Math.floor(Date.now() / 1000),
  }
  const encoder = new TextEncoder()
  const encodedHeader = base64url(encoder.encode(JSON.stringify(header)))
  const encodedPayload = base64url(encoder.encode(JSON.stringify(payload)))
  const toSign = encoder.encode(`${encodedHeader}.${encodedPayload}`)
  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privateKey,
    toSign
  )
  const encodedSignature = base64url(new Uint8Array(signature))
  return `${encodedHeader}.${encodedPayload}.${encodedSignature}`
}
